/**
 * ConfigPanel.js
 * ===============
 * Settings card embedded on LandingPage, positioned between the mode buttons
 * and the "How it works" section.
 *
 * Renders three configuration sections matching the Figma design (node 55:32):
 *   1. SELECTION OPTIONS  — radio: 3 blocks + back | 2 blocks + back
 *   2. CONFIRMATION BEHAVIOR — radio: long gaze & look away | double blink & look up
 *   3. SELECTION SPEED — 5-notch slider (−2 → +2, centre = default)
 *
 * All state is managed by GazeConfigContext — this component is purely a UI
 * control surface that calls updateConfig() on every change.
 */

import React, { useState, useEffect } from 'react';
import { useGazeConfig, COLOR_OPTIONS } from './GazeConfigContext';
import './ConfigPanel.css';

// ─── Section 1: Selection mode options ───────────────────────────────────────

const SELECTION_OPTIONS = [
  {
    value: '3-blocks',
    label: '3 BLOCKS + BACK',
    isDefault: true,
  },
  {
    value: '2-blocks',
    label: '2 BLOCKS + BACK',
    isDefault: false,
  },
];

// ─── Section 2: Confirmation behavior options ─────────────────────────────────

const CONFIRMATION_OPTIONS = [
  {
    value: 'long-gaze',
    label: 'LONG GAZE &\nLOOK AWAY TO DE-SELECT',
    isDefault: true,
  },
  {
    value: 'double-blink',
    label: 'DOUBLE BLINK &\nLOOK UP TO DE-SELECT',
    isDefault: false,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function ConfigPanel() {
  const { config, updateConfig } = useGazeConfig();

  // Novelty, character, and robotic voices excluded from AAC use.
  // Keeps natural-sounding system voices (Samantha, Daniel, Google US English, etc.)
  const AAC_BLOCKLIST = new Set([
    // macOS/iOS novelty & sound-effect voices
    'Bahh', 'Bells', 'Boing', 'Bubbles', 'Cellos', 'Cello',
    'Deranged', 'Good News', 'Hysterical', 'Organ', 'Pipe Organ',
    'Princess', 'Trinoids', 'Whisper', 'Wobble', 'Zarvox', 'Bad News',
    // macOS classic robotic voices
    'Albert', 'Fred', 'Junior', 'Kathy', 'Ralph',
    // macOS Monterey+ personality/character voices
    'Eddy', 'Flo', 'Jester', 'Reed', 'Rocko', 'Sandy', 'Shelley',
    'Superstar', 'Bruce',
  ]);

  // Load available English voices; re-populates when browser finishes async loading.
  const [voices, setVoices] = useState([]);
  useEffect(() => {
    function load() {
      const all = window.speechSynthesis?.getVoices() ?? [];
      setVoices(
        all
          .filter(v => v.lang.startsWith('en') && !AAC_BLOCKLIST.has(v.name))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    }
    load();
    if (window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = load;
      return () => { window.speechSynthesis.onvoiceschanged = null; };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="cp-card">

      {/* ── 1. Selection Options ──────────────────────────────────────── */}
      <div className="cp-section">
        <p className="cp-section-title">Selection Options</p>
        <div className="cp-options">
          {SELECTION_OPTIONS.map(opt => (
            <button
              key={opt.value}
              className={`cp-option ${config.selectionMode === opt.value ? 'cp-option--selected' : ''}`}
              onClick={() => updateConfig({ selectionMode: opt.value })}
              aria-pressed={config.selectionMode === opt.value}
            >
              <span className="cp-radio" aria-hidden="true" />
              <span className="cp-option-text">
                {/* Render newlines in label as line breaks */}
                <span className="cp-option-label">
                  {opt.label.split('\n').map((line, i, arr) => (
                    <span key={i}>
                      {line}
                      {i < arr.length - 1 && <br />}
                    </span>
                  ))}
                </span>
                {opt.isDefault && (
                  <span className="cp-option-default">Default</span>
                )}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── 2. Confirmation Behavior ──────────────────────────────────── */}
      <div className="cp-section">
        <p className="cp-section-title">Confirmation Behavior</p>
        <div className="cp-options">
          {CONFIRMATION_OPTIONS.map(opt => (
            <button
              key={opt.value}
              className={`cp-option ${config.confirmationMode === opt.value ? 'cp-option--selected' : ''}`}
              onClick={() => updateConfig({ confirmationMode: opt.value })}
              aria-pressed={config.confirmationMode === opt.value}
            >
              <span className="cp-radio" aria-hidden="true" />
              <span className="cp-option-text">
                <span className="cp-option-label">
                  {opt.label.split('\n').map((line, i, arr) => (
                    <span key={i}>
                      {line}
                      {i < arr.length - 1 && <br />}
                    </span>
                  ))}
                </span>
                {opt.isDefault && (
                  <span className="cp-option-default">Default</span>
                )}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── 3. Selection Speed ────────────────────────────────────────── */}
      <div className="cp-section">
        <p className="cp-section-title">Selection Speed</p>
        <div className="cp-slider-wrap">
          <div className="cp-slider-labels">
            <span className="cp-slider-label">Slower</span>
            <span className="cp-slider-label">Faster</span>
          </div>
          {/*
            5 discrete positions: min=-2, max=2, step=1.
            The thumb position maps directly to speedStep in the config.
          */}
          <input
            type="range"
            className="cp-slider"
            min="-2"
            max="2"
            step="1"
            value={config.speedStep}
            onChange={e => updateConfig({ speedStep: Number(e.target.value) })}
            aria-label="Selection speed"
          />
          {/* Visual notch markers to indicate the 5 discrete positions */}
          <div className="cp-slider-notches" aria-hidden="true">
            {[-2, -1, 0, 1, 2].map(step => (
              <span
                key={step}
                className={`cp-slider-notch ${step === 0 ? 'cp-slider-notch--center' : ''}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── 4. Read Aloud ────────────────────────────────────────────── */}
      <div className="cp-section">
        <p className="cp-section-title">Read Aloud Options</p>
        <p className="cp-section-desc">
          Announces each option before selection. Says "Look left for…", "Look right for…", etc.
        </p>

        {/* Toggle */}
        <button
          className={`cp-option ${config.readAloud ? 'cp-option--selected' : ''}`}
          onClick={() => {
            const next = !config.readAloud;
            updateConfig({ readAloud: next });
            // Speak an audible confirmation during this user gesture. Chrome
            // requires a real, audible utterance to establish the sticky session
            // activation — a silent/zero-volume utterance doesn't count, and
            // without this first in-gesture speak() all subsequent auto-speech
            // calls are silently blocked.
            if (next && window.speechSynthesis) {
              window.speechSynthesis.cancel();
              const primer = new SpeechSynthesisUtterance('Read aloud on');
              primer.rate   = 0.82;
              primer.volume = 1.0;
              const doSpeak = () => {
                if (config.voiceURI) {
                  const v = window.speechSynthesis.getVoices().find(v => v.voiceURI === config.voiceURI);
                  if (v) primer.voice = v;
                }
                window.speechSynthesis.speak(primer);
              };
              const currentVoices = window.speechSynthesis.getVoices();
              if (currentVoices.length > 0) {
                doSpeak();
              } else {
                window.speechSynthesis.onvoiceschanged = () => {
                  window.speechSynthesis.onvoiceschanged = null;
                  doSpeak();
                };
              }
            }
          }}
          aria-pressed={config.readAloud}
        >
          <span className="cp-radio" aria-hidden="true" />
          <span className="cp-option-text">
            <span className="cp-option-label">Read options aloud</span>
            {!config.readAloud && <span className="cp-option-default">Default off</span>}
          </span>
          <span className={`cp-toggle ${config.readAloud ? 'cp-toggle--on' : ''}`} aria-hidden="true">
            {config.readAloud ? 'ON' : 'OFF'}
          </span>
        </button>

        {/* Repeat count + voice — only shown when enabled */}
        {config.readAloud && (
          <>
            <div className="cp-repeat-row">
              <span className="cp-repeat-label">Repeat</span>
              <div className="cp-repeat-stepper">
                <button
                  className="cp-repeat-btn"
                  onClick={() => updateConfig({ readAloudRepeat: Math.max(0, config.readAloudRepeat - 1) })}
                  aria-label="Decrease repeat count"
                  disabled={config.readAloudRepeat === 0}
                >−</button>
                <span className="cp-repeat-count">{config.readAloudRepeat}</span>
                <button
                  className="cp-repeat-btn"
                  onClick={() => updateConfig({ readAloudRepeat: Math.min(10, config.readAloudRepeat + 1) })}
                  aria-label="Increase repeat count"
                >+</button>
              </div>
              <span className="cp-repeat-hint">
                {config.readAloudRepeat === 0 ? 'announce once' : `repeat ${config.readAloudRepeat}×`}
              </span>
            </div>

            <div className="cp-voice-row">
              <span className="cp-repeat-label">Voice</span>
              <select
                className="cp-voice-select"
                value={config.voiceURI}
                onChange={e => {
                  const uri = e.target.value;
                  updateConfig({ voiceURI: uri });
                  if (!window.speechSynthesis) return;
                  window.speechSynthesis.cancel();
                  const preview = new SpeechSynthesisUtterance('Hello, this is a preview of the selected voice.');
                  preview.rate = 0.82;
                  preview.volume = 1.0;
                  if (uri) {
                    const v = window.speechSynthesis.getVoices().find(v => v.voiceURI === uri);
                    if (v) preview.voice = v;
                  }
                  window.speechSynthesis.speak(preview);
                }}
                aria-label="Select voice for read aloud"
              >
                <option value="">Default (system voice)</option>
                {voices.map(v => (
                  <option key={v.voiceURI} value={v.voiceURI}>{v.name}</option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      {/* ── 5. Color Scheme ──────────────────────────────────────────── */}
      <div className="cp-section">
        <p className="cp-section-title">Color Scheme</p>

        <div className="cp-color-group">
          <p className="cp-color-label">Background</p>
          <div className="cp-swatches" role="group" aria-label="Background color">
            {COLOR_OPTIONS.map(opt => (
              <button
                key={opt.key}
                className={`cp-swatch ${config.backgroundColor === opt.key ? 'cp-swatch--selected' : ''}`}
                style={{ background: opt.hex }}
                onClick={() => updateConfig({ backgroundColor: opt.key })}
                aria-pressed={config.backgroundColor === opt.key}
                aria-label={opt.label}
                title={opt.label}
              />
            ))}
          </div>
        </div>

        <div className="cp-color-group">
          <p className="cp-color-label">Text</p>
          <div className="cp-swatches" role="group" aria-label="Text color">
            {COLOR_OPTIONS.map(opt => (
              <button
                key={opt.key}
                className={`cp-swatch ${config.textColor === opt.key ? 'cp-swatch--selected' : ''}`}
                style={{ background: opt.hex }}
                onClick={() => updateConfig({ textColor: opt.key })}
                aria-pressed={config.textColor === opt.key}
                aria-label={opt.label}
                title={opt.label}
              />
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
